import React, {useCallback, useEffect, useMemo, useState} from 'react';
import DocumentTitle from 'react-document-title';
import {useNavigate} from 'react-router-dom';

import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import {SortingDirections} from 'src/components/filter-header';
import {InternalServerError} from 'src/components/page-not-found';
import {useUserNameMapper} from 'src/components/user-name/hooks/useUserNameMapper';
import {FilterValue} from 'src/hooks/filtered-data/useFilteredData';
import {useFilters} from 'src/hooks/useFilters';
import {usePagination} from 'src/hooks/usePagination';
import {useSortingRows} from 'src/hooks/useSortingRows';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {ColumnTypes, SortingColumnTypes} from 'src/modules/change-requests/constants';
import {ChangeRequestsExcelDownload} from 'src/modules/change-requests/download/change-requests-excel-download';
import {
    columnCriteriaStripeData,
    dateCriteriaStripeData,
} from 'src/modules/change-requests/filter-utils/criteriaStripeData';
import {getChangeRequests} from 'src/modules/change-requests/services';
import {getChangeRequestsFilters} from 'src/modules/change-requests/services/filters';
import {
    clearChangeRequestsFilters,
    setAllChangeRequestsFilters,
    setChangeRequestsFilter,
} from 'src/modules/change-requests/services/filters/actions';
import {getFilteredChangeRequests} from 'src/modules/change-requests/services/selectors';
import {getReviewerStatuses} from 'src/modules/change-requests/utils/getReviewerStatuses';
import {userNameToUserEmail} from 'src/modules/change-requests/utils/userNameToUserEmail';
import {usersEmailsToUsersNames} from 'src/modules/change-requests/utils/usersEmailsToUsersNames';
import {ChangeRequestsView} from 'src/modules/change-requests/views/change-requests.view';
import {EventCreationMode} from 'src/modules/event-create/constants';
import {createEventCreationRoutePath} from 'src/modules/event-create/event-creation-route-planner';
import {createEventReviewRoutePath} from 'src/modules/event-review/event-review-route-planner';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {
    getAllChangeRequests,
    getAllChangeRequestsFailure,
    getAllChangeRequestsSuccess,
} from 'src/openapi/apis/ChangeRequestControllerApi';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestStatus} from 'src/openapi/models/ChangeRequestStatus';
import {Role} from 'src/openapi/models/Role';
import {useAppDispatch, useAppSelector} from 'src/store';
import {createColumnCriteria, createDateCriteria} from 'src/utils/criteriaStripeDataHelper';

import {Loading} from '@pb/kit-component-loading';

import {defaultState} from './services/filters/reducer';
import {Filters, ValueFilterKeys} from './typedefs';

type Props = {
    onlyMyFilter: boolean,
    setOnlyMyFilter: (value: boolean) => void,
    setShowOnlyMyFilter: (value: boolean) => void,
};

export const ChangeRequestsController = (props: Props) => {
    const {
        onlyMyFilter,
        setOnlyMyFilter,
        setShowOnlyMyFilter,
    } = props;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userEmail = useUserEmail();
    const userRoles = useUserRoles();
    const {filters} = useFilters<Filters>(
        getChangeRequestsFilters,
        clearChangeRequestsFilters,
        setAllChangeRequestsFilters,
    );
    const rows = useAppSelector(getChangeRequests);
    const [error, setError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {emailToName, nameToEmail} = useUserNameMapper();
    const editRequestStatuses = [ChangeRequestStatus.NEW, ChangeRequestStatus.REOPENED];
    const filteredRows = useAppSelector(getFilteredChangeRequests);
    const {sortConfig, onSort, sortedRows} = useSortingRows<ChangeRequest, SortingColumnTypes>(filteredRows);
    const {slicedRowsByPage, intersectRef, isVisible, hasMore} = usePagination<ChangeRequest>(sortedRows);

    const clearAllFilters = useCallback(() => {
        dispatch(clearChangeRequestsFilters());
    }, [dispatch]);

    const applyReviewerFilter = useCallback(() => {
        if (userRoles.includes(Role.ROLE_UDEDT_REVIEWER) && isEqual(filters, defaultState.data)) {
            dispatch(setChangeRequestsFilter({
                filterType: ColumnTypes.STATUS,
                filterValue: getReviewerStatuses(rows),
            }));
        }
    }, [userRoles, filters, rows, dispatch]);

    useEffect(() => {
        if (rows.length > 0) {
            applyReviewerFilter();
            onSort({key: SortingColumnTypes.LAST_UPDATED, direction: SortingDirections.ASC});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    useEffect(() => {
        setShowOnlyMyFilter(rows.some(({authorEmail}) => authorEmail === userEmail));
    }, [rows, setShowOnlyMyFilter, userEmail]);

    useEffect(() => {
        if (onlyMyFilter) {
            const authorFilterIncludesUserEmail = filters[ColumnTypes.AUTHOR].includes(userEmail);

            if (authorFilterIncludesUserEmail) {
                return;
            }

            setOnlyMyFilter(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        if (isEmpty(rows)) {
            setIsLoading(true);
            dispatch(getAllChangeRequests()).then(e => {
                if (e.type === getAllChangeRequestsSuccess.type) {
                    setIsLoading(false);
                }
                if (e.type === getAllChangeRequestsFailure.type) {
                    setError(true);
                    setIsLoading(false);
                }
            });
        }
    }, [dispatch, rows]);

    const createOrOpenEventReview = ({id: changeRequestId = 0}: ChangeRequest, link: boolean) => {
        const eventReviewRoutePath = createEventReviewRoutePath({changeRequestId});

        return link
            ? eventReviewRoutePath
            : navigate(eventReviewRoutePath);
    };

    const createOrOpenEventCreation = ({event: {id: eventId}, id: changeRequestId}: ChangeRequest, link: boolean) => {
        const mode = eventId ? EventCreationMode.update : EventCreationMode.create;
        const eventCreationRoutePath = createEventCreationRoutePath({mode, eventId: eventId ?? 0, changeRequestId});

        return link
            ? eventCreationRoutePath
            : navigate(eventCreationRoutePath);
    };

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const onClickChangeRequest = (changeRequest: ChangeRequest, link: boolean): string | void => {
        const {status, authorEmail} = changeRequest;
        if (editRequestStatuses.includes(status) && authorEmail === userEmail) {
            return createOrOpenEventCreation(changeRequest, link);
        }

        return createOrOpenEventReview(changeRequest, link);
    };

    const setFilter = useCallback((filterType: string, filterValue: FilterValue) =>
        dispatch(setChangeRequestsFilter({filterType, filterValue})), [dispatch]);

    const onPillRemove = useCallback((pillName: string, column: ColumnTypes) => {
        const pillValue = column === ColumnTypes.AUTHOR ? userNameToUserEmail(pillName, nameToEmail) : pillName;

        setFilter(column, filters[column as ValueFilterKeys].filter(value => value !== pillValue));
    }, [filters, nameToEmail, setFilter]);

    const criteriaStripeData = useMemo(() => {
        return [
            ...createColumnCriteria(
                {...filters, [ColumnTypes.AUTHOR]: usersEmailsToUsersNames(filters[ColumnTypes.AUTHOR], emailToName)},
                columnCriteriaStripeData,
                onPillRemove,
            ),
            ...createDateCriteria(filters, dateCriteriaStripeData, setFilter),
        ];
    }, [filters, emailToName, onPillRemove, setFilter]);

    if (error) {
        return (
            <InternalServerError/>
        );
    }

    if (isEmpty(rows) && isLoading) {
        return (
            <Loading fast={true}/>
        );
    }

    return (
        <>
            <DocumentTitle title="Change Requests"/>
            <UsersInfoController/>
            <ChangeRequestsExcelDownload
                filters={filters}
                changeRequests={sortedRows}
                showOnlyMy={onlyMyFilter}
            />
            <ChangeRequestsView {...{
                rows,
                preparedRows: slicedRowsByPage,
                setFilter,
                filters,
                onSort,
                sortConfig,
                criteriaStripeData,
                clearAllFilters,
                intersectRef,
                isVisible,
                hasMore,
                onClickChangeRequest,
                onlyMyFilter,
            }}
            />
        </>
    );
};
